import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useReducedMotion } from "../hooks/useReducedMotion";

interface FilterTag {
    id: string;
    icon: string;
    title: string;
}

interface FilterChipsProps {
    filterTags: FilterTag[];
    selectedFilters: string[];
    isDarkMode: boolean;
    onToggleFilter: (id: string) => void;
    className?: string;
}

const FilterChips: React.FC<FilterChipsProps> = ({
    filterTags,
    selectedFilters,
    isDarkMode,
    onToggleFilter,
    className = "",
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { shouldReduceMotion } = useReducedMotion();
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [hasDragged, setHasDragged] = useState(false);

    // Mouse Drag Logic
    const handleMouseDown = (e: React.MouseEvent) => {
        if (!scrollContainerRef.current) return;
        setIsDragging(true);
        setHasDragged(false);
        setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
        setScrollLeft(scrollContainerRef.current.scrollLeft);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging || !scrollContainerRef.current) return;
        e.preventDefault();
        const x = e.pageX - scrollContainerRef.current.offsetLeft;
        const walk = (x - startX) * 2; // Scroll-fastness
        scrollContainerRef.current.scrollLeft = scrollLeft - walk;
        if (Math.abs(x - startX) > 5) {
            setHasDragged(true); // Detect actual movement
        }
    };

    const handleFilterClick = (id: string) => {
        if (!hasDragged) {
            onToggleFilter(id);
        }
    };

    return (
        <div className={`w-full ${className}`}>
            <div
                ref={scrollContainerRef}
                className={`flex items-center gap-3 overflow-x-auto scrollbar-hide px-1 py-2 cursor-grab ${isDragging ? 'cursor-grabbing' : ''}`}
                style={{ scrollBehavior: isDragging ? 'auto' : 'smooth' }}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
            >
                {filterTags.map((tag) => {
                    const isSelected = selectedFilters.includes(tag.id);

                    return (
                        <motion.button
                            key={tag.id}
                            initial={false} // Disable initial mounting animation to prevent "pop-in"
                            onClick={() => handleFilterClick(tag.id)}
                            whileTap={{ scale: 0.95 }}
                            animate={{
                                backgroundColor: isSelected
                                    ? (isDarkMode ? '#2563eb' : '#2563eb')
                                    : (isDarkMode ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.95)'),
                                color: isSelected ? '#ffffff' : (isDarkMode ? '#e5e7eb' : '#1f2937'),
                                borderColor: isSelected
                                    ? (isDarkMode ? '#3b82f6' : '#2563eb')
                                    : (isDarkMode ? 'rgba(55, 65, 81, 0.2)' : 'rgba(229, 231, 235, 1)'),
                            }}
                            transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.2 }}
                            className={`
                flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl border shadow-md backdrop-blur-sm
                text-sm font-medium transition-all select-none min-w-[max-content]
                ${isSelected ? 'ring-2 ring-blue-500/20' : 'hover:scale-105'}
              `}
                        >
                            <span className="text-lg leading-none select-none pointer-events-none" role="img" aria-label={tag.title}>
                                {tag.icon}
                            </span>
                            <span className="pointer-events-none">{tag.title}</span>
                        </motion.button>
                    );
                })}
            </div>
        </div>
    );
};

export default FilterChips;
